/**
 * Filter utilities for handling filter state, URL params, and other filter operations
 */
import dayjs from 'dayjs';

/**
 * Parse an array parameter from URL or query string
 * @param {string|Array} param - Parameter to parse
 * @returns {Array} Array of values
 */
export const parseArrayParam = (param) => {
  if (!param) return [];
  return Array.isArray(param) ? param : [param];
};

/**
 * Format a date for display in filter UI
 * @param {string|Object} date - Date to format (string or dayjs object)
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (date) => {
  if (!date) return '';
  return dayjs(date).format('MM/DD/YYYY');
};

/**
 * Format a date for API requests
 * @param {string|Object} date - Date to format (string or dayjs object)
 * @returns {string} ISO date string
 */
export const formatDateForAPI = (date) => {
  if (!date) return null;
  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * Get human-readable filter description
 * @param {Object} filters - Current filter state
 * @param {Object} options - Options containing role names, provider names
 * @returns {string} Human-readable filter description
 */
export const getFilterDescription = (filters, options = {}) => {
  const { roleLabels = {}, providerLabels = {} } = options;
  const parts = [];
  
  if (filters.search) {
    parts.push(`Search: "${filters.search}"`);
  }
  
  if (filters.isActive !== null) {
    parts.push(`Status: ${filters.isActive ? 'Active' : 'Inactive'}`);
  }
  
  if (filters.roles && filters.roles.length > 0) {
    const roleNames = filters.roles.map(role => roleLabels[role] || role).join(', ');
    parts.push(`Roles: ${roleNames}`);
  }
  
  if (filters.providers && filters.providers.length > 0) {
    const providerNames = filters.providers.map(provider => providerLabels[provider] || provider).join(', ');
    parts.push(`Providers: ${providerNames}`);
  }
  
  if (filters.dateRange?.start || filters.dateRange?.end) {
    let dateText = 'Created: ';
    if (filters.dateRange.start && filters.dateRange.end) {
      dateText += `${formatDateForDisplay(filters.dateRange.start)} to ${formatDateForDisplay(filters.dateRange.end)}`;
    } else if (filters.dateRange.start) {
      dateText += `after ${formatDateForDisplay(filters.dateRange.start)}`;
    } else if (filters.dateRange.end) {
      dateText += `before ${formatDateForDisplay(filters.dateRange.end)}`;
    }
    parts.push(dateText);
  }
  
  if (parts.length === 0) {
    return 'No filters applied';
  }
  
  return `Filtered by: ${parts.join(' • ')}`;
};

/**
 * Check if a filter has any active values
 * @param {Object} filters - Current filter state
 * @returns {boolean} True if any filters are active
 */
export const hasActiveFilters = (filters) => {
  if (!filters) return false;
  
  return !!(
    filters.search ||
    filters.isActive !== null ||
    (filters.roles && filters.roles.length > 0) ||
    (filters.providers && filters.providers.length > 0) ||
    filters.dateRange?.start ||
    filters.dateRange?.end
  );
};

/**
 * Serialize filters for URL parameters
 * @param {Object} filters - Filter state to serialize
 * @returns {Object} URL parameter object
 */
export const serializeFilters = (filters) => {
  const params = {};
  
  if (filters.search) params.search = filters.search;
  
  if (filters.isActive !== null) {
    params.isActive = filters.isActive.toString();
  }
  
  if (filters.roles && filters.roles.length > 0) {
    params.roles = filters.roles;
  }
  
  if (filters.providers && filters.providers.length > 0) {
    params.providers = filters.providers;
  }
  
  if (filters.dateRange?.start) {
    params.createdDateStart = formatDateForAPI(filters.dateRange.start);
  }
  
  if (filters.dateRange?.end) {
    params.createdDateEnd = formatDateForAPI(filters.dateRange.end);
  }
  
  if (filters.sortBy) {
    params.sortBy = filters.sortBy;
    params.sortOrder = filters.sortOrder || 'desc';
  }
  
  if (filters.page !== 1) {
    params.page = filters.page.toString();
  }
  
  if (filters.limit !== 10) {
    params.limit = filters.limit.toString();
  }
  
  return params;
};

/**
 * Deserialize URL parameters into filter state
 * @param {Object} queryParams - URL query parameters
 * @returns {Object} Filter state object
 */
export const deserializeFilters = (queryParams) => {
  return {
    search: queryParams.get('search') || '',
    isActive: queryParams.has('isActive') ? 
              queryParams.get('isActive') === 'true' : null,
    roles: parseArrayParam(queryParams.getAll('roles')),
    providers: parseArrayParam(queryParams.getAll('providers')),
    dateRange: {
      start: queryParams.get('createdDateStart') || null,
      end: queryParams.get('createdDateEnd') || null
    },
    sortBy: queryParams.get('sortBy') || null,
    sortOrder: queryParams.get('sortOrder') || 'desc',
    page: parseInt(queryParams.get('page'), 10) || 1,
    limit: parseInt(queryParams.get('limit'), 10) || 10
  };
}; 